import { Project } from '@/types/project';
import { formatCompactCurrency, formatCompactNumber } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  DollarSign, 
  Calendar, 
  Users, 
  TrendingUp,
  Building
} from 'lucide-react';
import FundingRoundDropdown from './FundingRoundDropdown';

interface ProjectOverviewProps {
  project: Project;
  loading?: boolean;
  onPlaceInquiry?: () => void;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({ project, loading = false }) => {
  // Calculate total funding raised from rounds
  const totalFunding = (project.rounds || []).reduce((sum, round) => sum + (round.amount_raised || 0), 0);
  const latestRound = (project.rounds || []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  const currentValuation = latestRound?.fully_diluted_valuation || 0;

  // Get unique investors from all rounds
  const allInvestors = Array.from(
    new Set(
      (project.rounds || [])
        .flatMap(round => round.investors || [])
        .filter(investor => investor.trim())
    )
  );

  const statCards = [
    {
      label: 'Total Funding Raised',
      value: totalFunding > 0 ? formatCompactCurrency(totalFunding) : '—',
      icon: <DollarSign size={16} />,
      description: 'Total amount raised across all rounds'
    },
    {
      label: 'Latest Round',
      value: latestRound ? latestRound.round_name : '—',
      subValue: latestRound?.fully_diluted_valuation ? `Valuation: ${formatCompactCurrency(latestRound.fully_diluted_valuation)}` : undefined,
      icon: <TrendingUp size={16} />,
      description: 'Most recent funding round'
    },
    {
      label: 'Current Valuation',
      value: currentValuation > 0 ? formatCompactCurrency(currentValuation) : '—',
      icon: <Building size={16} />,
      description: 'Latest fully diluted valuation'
    },
    {
      label: 'Funding Round',
      value: formatCompactNumber((project.rounds || []).length),
      icon: <Calendar size={16} />,
      description: 'Total number of funding rounds'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards - 4 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          // Loading skeletons for stats cards
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-[#121212] h-[64px] p-4 animate-pulse" />
          ))
        ) : (
          statCards.map((stat, index) => (
            <div 
              key={index} 
              className="bg-white dark:bg-[#121212] h-[64px] flex items-center justify-between px-4"
            >
              {/* Left side: icon, label, description */}
              <div className="flex items-center gap-4">
                <div className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {stat.icon}
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-[6px]">
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      {stat.label}
                    </span>
                  </div>
                  <span className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                    {stat.description}
                  </span>
                </div>
              </div>
              {/* Right side: value */}
              <div className="flex flex-col items-end">
                <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {stat.value}
                </span>
                {stat.subValue && (
                  <span className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">
                    {stat.subValue}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Two Column Layout: Funding Rounds (left) + Investors (right) */}
      <div className="flex gap-4">
        {/* Funding Rounds Section - Left */}
        <div className="flex-[2] min-w-0">
          <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)]">
            <div className="p-8">
              <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mb-6">
                Funding Rounds
              </h2>
              
              {loading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <Skeleton className="w-12 h-12 rounded-lg" />
                          <div>
                            <Skeleton className="h-4 w-32 rounded mb-2" />
                            <Skeleton className="h-3 w-24 rounded" />
                          </div>
                        </div>
                        <Skeleton className="h-4 w-20 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                (project.rounds || []).length > 0 ? (
                  <div className="space-y-4">
                    {(project.rounds || []).map((round, index) => (
                      <FundingRoundDropdown key={index} round={round} index={index} />
                    ))}
                  </div>
                ) : (
                  <p className="font-['Plus_Jakarta_Sans'] text-[14px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] italic">
                    No funding rounds data available
                  </p>
                )
              )}
            </div>
          </div>
        </div>

        {/* Investors Section - Right */}
        {allInvestors.length > 0 && (
          <div className="flex-1 min-w-[300px] max-w-[520px]">
            <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)]">
              <div className="p-8">
                <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mb-6">
                  Investors
                </h2>
                
                <div className="flex flex-wrap gap-[6px]">
                  {allInvestors.map((investor, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-[6px] bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] border border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.08)] px-3 py-[10px]"
                    >
                      <Users size={14} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
                      <span className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {investor}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectOverview; 